import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import axios from "axios";
import { toast } from "sonner";
import { Briefcase, ArrowLeft, Send, Mail, MessageSquare, CheckCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactPage() {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "general",
    order_id: "",
    message: ""
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.message) {
      toast.error("Please fill in all required fields");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/contact`, formData);
      setSubmitted(true);
      toast.success("Message sent successfully!");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send message. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-zinc-50">
        <nav className="bg-white border-b border-zinc-200">
          <div className="max-w-4xl mx-auto px-6 py-4">
            <Link to="/" className="flex items-center gap-2">
              <Briefcase className="w-6 h-6 text-[#002FA7]" />
              <span className="font-black text-xl tracking-tight text-zinc-900">GetJobInterviews</span>
            </Link>
          </div>
        </nav>
        
        <main className="max-w-xl mx-auto px-6 py-16 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
          <h1 className="text-2xl font-bold text-zinc-900 mb-4">Message Sent!</h1>
          <p className="text-zinc-600 mb-8">
            Thanks for reaching out. We'll get back to you within 24-48 hours.
          </p>
          <Link to="/">
            <Button className="bg-[#002FA7] text-white hover:bg-[#002280] rounded-sm">
              Back to Home
            </Button>
          </Link>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-50">
      {/* Navigation */}
      <nav className="bg-white border-b border-zinc-200">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <Briefcase className="w-6 h-6 text-[#002FA7]" />
            <span className="font-black text-xl tracking-tight text-zinc-900">GetJobInterviews</span>
          </Link>
          <Link to="/" className="flex items-center gap-2 text-zinc-600 hover:text-zinc-900">
            <ArrowLeft className="w-4 h-4" /> Back
          </Link>
        </div>
      </nav>

      <main className="max-w-2xl mx-auto px-6 py-12">
        <div className="text-center mb-10">
          <h1 className="text-3xl font-bold text-zinc-900 mb-4">Contact Us</h1>
          <p className="text-zinc-600">
            Have a question, feedback, or need help with your order? We're here to help.
          </p>
        </div>

        {/* Contact Options */}
        <div className="grid md:grid-cols-2 gap-4 mb-10">
          <div className="bg-white border border-zinc-200 p-6 text-center">
            <Mail className="w-8 h-8 text-[#002FA7] mx-auto mb-3" />
            <h3 className="font-semibold text-zinc-900 mb-2">Email Us</h3>
            <a href="mailto:support@getjobinterviews.com" className="text-[#002FA7] hover:underline">
              support@getjobinterviews.com
            </a>
          </div>
          <div className="bg-white border border-zinc-200 p-6 text-center">
            <MessageSquare className="w-8 h-8 text-[#002FA7] mx-auto mb-3" />
            <h3 className="font-semibold text-zinc-900 mb-2">Response Time</h3>
            <p className="text-zinc-600">Within 24-48 hours</p>
          </div>
        </div>

        {/* Contact Form */}
        <form onSubmit={handleSubmit} className="bg-white border border-zinc-200 p-6">
          <h2 className="text-lg font-semibold text-zinc-900 mb-6">Send us a Message</h2>
          
          <div className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Your Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="John Doe"
                  required
                  className="rounded-sm"
                  data-testid="contact-name"
                />
              </div>
              <div className="space-y-2">
                <Label>Your Email *</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="john@example.com"
                  required
                  className="rounded-sm"
                  data-testid="contact-email"
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Subject</Label>
                <Select 
                  value={formData.subject}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, subject: value }))}
                >
                  <SelectTrigger className="rounded-sm" data-testid="contact-subject">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="general">General Inquiry</SelectItem>
                    <SelectItem value="order">Order Status</SelectItem>
                    <SelectItem value="redo">Request Refinement</SelectItem>
                    <SelectItem value="refund">Refund Request</SelectItem>
                    <SelectItem value="feedback">Feedback</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Order ID (if applicable)</Label>
                <Input
                  value={formData.order_id}
                  onChange={(e) => setFormData(prev => ({ ...prev, order_id: e.target.value }))}
                  placeholder="e.g., abc123..."
                  className="rounded-sm"
                  data-testid="contact-order-id"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Message *</Label>
              <Textarea
                value={formData.message}
                onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
                placeholder="How can we help you?"
                required
                className="rounded-sm min-h-[150px]"
                data-testid="contact-message"
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-[#002FA7] text-white hover:bg-[#002280] rounded-sm py-6"
              data-testid="contact-submit"
            >
              {loading ? "Sending..." : (
                <>
                  <Send className="w-4 h-4 mr-2" /> Send Message
                </>
              )}
            </Button>
          </div>
        </form>

        {/* FAQ Quick Links */}
        <div className="mt-10 bg-white border border-zinc-200 p-6">
          <h2 className="text-lg font-semibold text-zinc-900 mb-4">Common Questions</h2>
          <ul className="space-y-3 text-zinc-700">
            <li className="flex items-start gap-2">
              <span className="text-[#002FA7] font-bold">Q:</span>
              <span><strong>How long does delivery take?</strong> — Usually within 3 business days.</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-[#002FA7] font-bold">Q:</span>
              <span><strong>What if I'm not satisfied?</strong> — We offer one free refinement of your batch.</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-[#002FA7] font-bold">Q:</span>
              <span><strong>Can I get a refund?</strong> — Yes, before work begins. See our <Link to="/terms" className="text-[#002FA7] hover:underline">Terms</Link>.</span>
            </li>
          </ul>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-zinc-900 text-white mt-12">
        <div className="max-w-4xl mx-auto px-6 py-8 text-center">
          <p className="text-sm text-zinc-400">
            © {new Date().getFullYear()} GetJobInterviews.com. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
